import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  ComposedChart, Line, ReferenceLine, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { getWarrantyRatioColor, WARRANTY_RATIO_TARGET } from './warrantyUtils';
import LoadingSpinner from './components/LoadingSpinner';
import './CurrentStatsPage.css';

const API_BASE = process.env.REACT_APP_API_URL;

const PRODUCT_COLORS = [
  '#63B3ED', '#F6AD55', '#FC8181', '#B794F4',
  '#76E4F7', '#F6E05E', '#FBB6CE', '#FB923C',
  '#A78BFA', '#34D399',
];

// ── API helpers ───────────────────────────────────────────────────────────────
async function authHeaders(getToken) {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchRatio(getToken) {
  const r = await fetch(`${API_BASE}/analytics/warranty-ratio`, {
    headers: await authHeaders(getToken),
  });
  if (!r.ok) throw new Error('Failed to fetch ratio');
  return r.json();
}

async function fetchHistory(getToken, breakdown = false) {
  const url = `${API_BASE}/analytics/warranty-ratio/history${breakdown ? '?breakdown=1' : ''}`;
  const r = await fetch(url, { headers: await authHeaders(getToken) });
  if (!r.ok) throw new Error('Failed to fetch history');
  return r.json();
}

async function fetchAiInsights(payload, signal, getToken) {
  const r = await fetch(`${API_BASE}/analytics/warranty-ratio/ai-insights`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders(getToken)),
    },
    body: JSON.stringify(payload),
    signal,
  });
  if (!r.ok) throw new Error('AI analysis failed');
  return r.json();
}

async function createIcebergCase(payload, getToken) {
  const r = await fetch(`${API_BASE}/iceberg`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders(getToken)),
    },
    body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error('Failed to create case');
  return r.json();
}

// ── Sub-components ────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color }) {
  return (
    <div className="cs-stat-card">
      <span className="cs-stat-label">{label}</span>
      <strong className="cs-stat-value" style={color ? { color } : {}}>{value}</strong>
      {sub && <span className="cs-stat-sub">{sub}</span>}
    </div>
  );
}

function IcebergBadge({ tier }) {
  const colors = {
    event: { bg: 'rgba(252,129,129,0.12)', border: 'rgba(252,129,129,0.35)', text: '#FC8181' },
    pattern: { bg: 'rgba(246,173,85,0.12)', border: 'rgba(246,173,85,0.35)', text: '#F6AD55' },
    structure: { bg: 'rgba(99,179,237,0.12)', border: 'rgba(99,179,237,0.35)', text: '#63B3ED' },
    'mental-model': { bg: 'rgba(70,187,137,0.12)', border: 'rgba(70,187,137,0.35)', text: '#46BB89' },
  };
  const c = colors[tier] || colors.event;
  return (
    <span className="cs-iceberg-badge" style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text }}>
      {tier}
    </span>
  );
}

function CustomTooltip({ active, payload, label, topProducts, activeProducts }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  const pct = ((d?.ratio || 0) * 100).toFixed(2);
  const activePns = (topProducts || []).filter(pn => (activeProducts || new Set()).has(pn));
  return (
    <div className="cs-tooltip">
      <div className="cs-tooltip-label">{label}</div>
      <div className="cs-tooltip-value" style={{ color: getWarrantyRatioColor(d?.ratio || 0) }}>
        {pct}% overall
      </div>
      {activePns.map((pn, i) => d?.[pn] != null && (
        <div key={pn} className="cs-tooltip-pn" style={{ color: PRODUCT_COLORS[i % PRODUCT_COLORS.length] }}>
          {(d[pn] * 100).toFixed(2)}% <span style={{ opacity: 0.55 }}>{pn}</span>
        </div>
      ))}
      <div className="cs-tooltip-sub">Target &lt; {(WARRANTY_RATIO_TARGET * 100).toFixed(1)}%</div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function CurrentStatsPage() {
  const { getToken } = useAuth();
  const [data, setData]             = useState(null);
  const [history, setHistory]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [view, setView]             = useState('kiosk'); // 'kiosk' | 'graph'
  const [showDetail, setShowDetail] = useState(false);

  // By-product breakdown
  const [showBreakdown, setShowBreakdown]   = useState(false);
  const [topProducts, setTopProducts]       = useState([]);
  const [productSeries, setProductSeries]   = useState({});
  const [activeProducts, setActiveProducts] = useState(new Set());

  // AI state
  const [aiData, setAiData]         = useState(null);
  const [aiLoading, setAiLoading]   = useState(false);
  const [aiError, setAiError]       = useState(null);
  const [aiStale, setAiStale]       = useState(false);
  const aiAbortRef                  = useRef(null);

  // Iceberg auto-create
  const [creatingCase, setCreatingCase] = useState(null); // productNumber being escalated
  const [caseCreated, setCaseCreated]   = useState({});   // { [pn]: caseId }

  // ── Load ratio + history ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([fetchRatio(getToken), fetchHistory(getToken, showBreakdown)])
      .then(([ratio, hist]) => {
        if (cancelled) return;
        setData(ratio);
        if (showBreakdown && hist && !Array.isArray(hist)) {
          setHistory(Array.isArray(hist.history) ? hist.history : []);
          const bkd = hist.breakdown || {};
          setTopProducts(bkd.topProducts || []);
          setProductSeries(bkd.series || {});
          setActiveProducts(new Set(bkd.topProducts || []));
        } else {
          setHistory(Array.isArray(hist) ? hist : []);
          setTopProducts([]);
          setProductSeries({});
          setActiveProducts(new Set());
        }
        setLoading(false);
        setAiStale(true);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [getToken, showBreakdown]);

  // ── AI analysis ─────────────────────────────────────────────────────────────
  const runAiAnalysis = useCallback(() => {
    if (!data) return;
    if (aiAbortRef.current) aiAbortRef.current.abort();
    const ctrl = new AbortController();
    aiAbortRef.current = ctrl;

    setAiLoading(true);
    setAiError(null);
    setAiStale(false);

    fetchAiInsights({
      ratio:          data.ratio,
      deviceCount:    data.deviceCount,
      rmaCount:       data.rmaCount,
      topProducts:    data.topProducts,
      monthlyHistory: history.map(h => ({ date: h.month, ratio: h.ratio, rmaCount: 0, deviceCount: 0 })),
    }, ctrl.signal, getToken)
      .then(res => {
        setAiData(res);
        setAiLoading(false);
      })
      .catch(err => {
        if (err.name === 'AbortError') return;
        setAiError(err.message);
        setAiLoading(false);
      });
  }, [data, history, getToken]);

  // Auto-run AI when data first loads
  useEffect(() => {
    if (data && aiStale && !aiLoading) runAiAnalysis();
  }, [data, aiStale]); // eslint-disable-line

  // ── Auto-create Iceberg cases from AI suggestions ───────────────────────────
  const handleAutoCreate = useCallback(async (suggestion) => {
    const pn = suggestion.productNumbers?.[0] || 'unknown';
    setCreatingCase(pn);
    try {
      const res = await createIcebergCase({
        title:          suggestion.title,
        description:    suggestion.description,
        productNumbers: suggestion.productNumbers || [],
        origin:         'ai',
      }, getToken);
      setCaseCreated(prev => ({ ...prev, [pn]: res.case?._id }));
    } catch (_) {
      // silently fail — user can create manually
    } finally {
      setCreatingCase(null);
    }
  }, [getToken]);

  const toggleProduct = useCallback(pn => {
    setActiveProducts(prev => {
      const next = new Set(prev);
      if (next.has(pn)) next.delete(pn); else next.add(pn);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setActiveProducts(prev =>
      prev.size === topProducts.length ? new Set() : new Set(topProducts)
    );
  }, [topProducts]);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const color = data ? getWarrantyRatioColor(data.ratio) : '#9ca3af';
  const ratioStr = data ? `${(data.ratio * 100).toFixed(2)}%` : '—';

  const chartData = useMemo(() => {
    const byMonth = {};
    history.forEach(h => { byMonth[h.month] = { name: h.month, ratio: h.ratio }; });
    if (showBreakdown) {
      Object.entries(productSeries).forEach(([pn, pts]) => {
        pts.forEach(({ month, ratio }) => {
          if (byMonth[month]) byMonth[month][pn] = ratio;
        });
      });
    }
    return Object.values(byMonth).sort((a, b) => a.name < b.name ? -1 : 1);
  }, [history, productSeries, showBreakdown]);

  // Trend: compare last 3 months vs 3 months before that
  const last3  = history.slice(-3).reduce((s, h) => s + h.ratio, 0) / 3;
  const prev3  = history.slice(-6, -3).reduce((s, h) => s + h.ratio, 0) / 3;
  const trendDir = last3 > prev3 * 1.05 ? '↑' : last3 < prev3 * 0.95 ? '↓' : '→';
  const trendColor = trendDir === '↑' ? '#FC8181' : trendDir === '↓' ? '#46BB89' : '#F6AD55';

  if (loading) {
    return (
      <div className="cs-page">
        <div className="cs-loading"><LoadingSpinner size={56} /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cs-page">
        <div className="cs-error">Failed to load: {error}</div>
      </div>
    );
  }

  return (
    <div className="cs-page">
      {/* ── View toggle ─────────────────────────────────────────────────────── */}
      <div className="cs-view-toggle">
        <button className={`cs-toggle-btn ${view === 'kiosk' ? 'active' : ''}`} onClick={() => setView('kiosk')}>
          ◉ Kiosk
        </button>
        <button className={`cs-toggle-btn ${view === 'graph' ? 'active' : ''}`} onClick={() => setView('graph')}>
          ▲ Graph
        </button>
      </div>

      {/* ═══════════════════════════════════ KIOSK VIEW ══════════════════════ */}
      {view === 'kiosk' && (
        <div className="cs-kiosk">
          {/* Main ratio */}
          <div className="cs-kiosk-hero">
            <span className="cs-kiosk-label">Current warranty RMA ratio</span>
            <span className="cs-kiosk-ratio" style={{ color }}>{ratioStr}</span>
            <span className="cs-kiosk-target">target &lt; {(WARRANTY_RATIO_TARGET * 100).toFixed(1)}%</span>
            <span className="cs-kiosk-trend" style={{ color: trendColor }}>
              {trendDir} {trendDir === '↑' ? 'Worsening trend' : trendDir === '↓' ? 'Improving trend' : 'Stable'}
            </span>
          </div>

          {/* Detail toggle */}
          <button className="cs-detail-toggle" onClick={() => setShowDetail(v => !v)}>
            {showDetail ? '▲ Hide details' : '▼ Show details'}
          </button>

          {showDetail && data && (
            <div className="cs-kiosk-stats">
              <StatCard label="Devices within warranty" value={data.deviceCount.toLocaleString()} />
              <StatCard label="Accepted warranty claims" value={data.rmaCount.toLocaleString()} />
              <StatCard
                label="Worst product"
                value={data.worstProduct?.productNumber || '—'}
                sub={data.worstProduct ? `${data.worstProduct.claimCount} claims` : undefined}
                color="#F6AD55"
              />
              <StatCard label="Trend vs last month" value={trendDir} color={trendColor} />
            </div>
          )}

          {/* Top products */}
          {showDetail && data?.topProducts?.length > 0 && (
            <div className="cs-top-products">
              <div className="cs-section-head">Top products by warranty claims</div>
              <div className="cs-product-rows">
                {data.topProducts.map(p => (
                  <div key={p.productNumber} className="cs-product-row">
                    <span className="cs-product-pn">{p.productNumber}</span>
                    <span className="cs-product-bar-wrap">
                      <span
                        className="cs-product-bar"
                        style={{ width: `${Math.min(100, (p.claimCount / (data.topProducts[0]?.claimCount || 1)) * 100)}%` }}
                      />
                    </span>
                    <span className="cs-product-count">{p.claimCount}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════ GRAPH VIEW ══════════════════════ */}
      {view === 'graph' && (
        <div className="cs-graph-section">
          <div className="cs-graph-card">
            <div className="cs-graph-header">
              <span className="cs-graph-title">Warranty ratio — monthly trend</span>
              <span className="cs-graph-sub">Target: {(WARRANTY_RATIO_TARGET * 100).toFixed(1)}%</span>
            </div>

            {/* Stat row + breakdown toggle */}
            <div className="cs-graph-controls-row">
              <div className="cs-kiosk-stats" style={{ flex: 1, marginBottom: 0 }}>
                <StatCard label="Current ratio" value={ratioStr} color={color} />
                <StatCard label="Devices in warranty" value={data?.deviceCount?.toLocaleString() || '—'} />
                <StatCard label="Claims (active warranty)" value={data?.rmaCount?.toLocaleString() || '—'} />
                <StatCard label="Trend" value={trendDir} color={trendColor} />
              </div>
              <button
                type="button"
                className={`cs-breakdown-toggle${showBreakdown ? ' active' : ''}`}
                onClick={() => setShowBreakdown(v => !v)}
                title="Overlay top 10 product lines"
              >
                <span className="cs-breakdown-icon">⊞</span>
                By product
                {showBreakdown && topProducts.length > 0 && (
                  <span className="cs-breakdown-badge">{topProducts.length}</span>
                )}
              </button>
            </div>

            {/* Product legend */}
            {showBreakdown && topProducts.length > 0 && (
              <div className="cs-legend">
                <button
                  type="button"
                  className={`cs-legend-toggle-all${activeProducts.size === topProducts.length ? ' active' : ''}`}
                  onClick={toggleAll}
                >
                  {activeProducts.size === topProducts.length ? 'Hide all' : 'Show all'}
                </button>
                <div className="cs-legend-chips">
                  {topProducts.map((pn, i) => {
                    const c = PRODUCT_COLORS[i % PRODUCT_COLORS.length];
                    const isOn = activeProducts.has(pn);
                    return (
                      <button
                        key={pn}
                        type="button"
                        className={`cs-legend-chip${isOn ? ' active' : ''}`}
                        style={isOn ? { borderColor: c, color: c, background: `${c}18` } : {}}
                        onClick={() => toggleProduct(pn)}
                      >
                        <span className="cs-legend-dot" style={{ background: isOn ? c : 'rgba(255,255,255,0.2)' }} />
                        {pn}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="cs-chart-wrap">
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={chartData} margin={{ top: 8, right: 24, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tickFormatter={v => `${(v * 100).toFixed(1)}%`}
                    tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={52}
                  />
                  <Tooltip content={
                    <CustomTooltip topProducts={topProducts} activeProducts={activeProducts} />
                  } />
                  <ReferenceLine
                    y={WARRANTY_RATIO_TARGET}
                    stroke="#FC8181"
                    strokeDasharray="6 3"
                    label={{ value: 'Target 0.2%', fill: '#FC8181', fontSize: 11, position: 'insideTopRight' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="ratio"
                    stroke={showBreakdown ? 'rgba(99,179,237,0.35)' : '#63B3ED'}
                    strokeWidth={showBreakdown ? 1.5 : 2}
                    dot={false}
                    name="Overall ratio"
                  />
                  {showBreakdown && topProducts.filter(pn => activeProducts.has(pn)).map((pn, i) => (
                    <Line
                      key={pn}
                      type="monotone"
                      dataKey={pn}
                      stroke={PRODUCT_COLORS[i % PRODUCT_COLORS.length]}
                      strokeWidth={1.5}
                      dot={false}
                      name={pn}
                    />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── AI Analysis ─────────────────────────────────────────────────── */}
          <div className="cs-ai-card">
            <div className="cs-ai-header">
              <span className="cs-ai-badge"><span className="cs-ai-star">◈</span> AI Trend Analysis</span>
              <button
                className="cs-ai-refresh"
                onClick={runAiAnalysis}
                disabled={aiLoading}
              >
                ↺ Refresh
              </button>
            </div>

            {aiLoading && (
              <div className="cs-ai-thinking">
                <div className="cs-ai-orb"><span className="cs-ai-spin">◈</span></div>
                <div>
                  <div className="cs-ai-thinking-title">Analysing warranty trends…</div>
                  <div className="cs-ai-thinking-sub">Applying Iceberg Model framework</div>
                </div>
              </div>
            )}

            {!aiLoading && aiError && (
              <div className="cs-ai-error">{aiError}</div>
            )}

            {!aiLoading && !aiError && aiData && (
              <div className="cs-ai-body">
                {/* Summary */}
                {aiData.summary && <p className="cs-ai-summary">{aiData.summary}</p>}

                {/* Indicators */}
                {aiData.indicators?.length > 0 && (
                  <div className="cs-indicators">
                    {aiData.indicators.map((ind, i) => {
                      const sentColor = ind.sentiment === 'positive' ? '#46BB89'
                        : ind.sentiment === 'negative' ? '#FC8181'
                        : ind.sentiment === 'warning'  ? '#F6AD55' : '#63B3ED';
                      const dirIcon = { up: '↑', down: '↓', flat: '→', spike: '⚡' }[ind.direction] || '→';
                      return (
                        <div key={i} className="cs-indicator" style={{ borderColor: `${sentColor}40`, background: `${sentColor}0a` }}>
                          <span className="cs-indicator-dir" style={{ color: sentColor }}>{dirIcon}</span>
                          <div className="cs-indicator-body">
                            <span className="cs-indicator-value" style={{ color: sentColor }}>{ind.value}</span>
                            <span className="cs-indicator-label">{ind.label}</span>
                            {ind.note && <span className="cs-indicator-note">{ind.note}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Positives + Concerns */}
                <div className="cs-ai-sections">
                  {aiData.positives?.length > 0 && (
                    <div className="cs-ai-section">
                      <div className="cs-ai-section-head cs-ai-section-head--positive">Positives</div>
                      <ul className="cs-ai-list">{aiData.positives.map((p, i) => <li key={i}>{p}</li>)}</ul>
                    </div>
                  )}
                  {aiData.concerns?.length > 0 && (
                    <div className="cs-ai-section cs-ai-section--risk">
                      <div className="cs-ai-section-head cs-ai-section-head--risk">Concerns</div>
                      <ul className="cs-ai-list">{aiData.concerns.map((c, i) => <li key={i}>{c}</li>)}</ul>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {aiData.actions?.length > 0 && (
                  <div className="cs-ai-actions">
                    <div className="cs-ai-section-head">Recommended actions</div>
                    <ol className="cs-ai-list cs-ai-list--ordered">
                      {aiData.actions.map((a, i) => <li key={i}>{a}</li>)}
                    </ol>
                  </div>
                )}

                {/* Iceberg Model tiers */}
                {aiData.iceberg && (
                  <div className="cs-iceberg-section">
                    <div className="cs-iceberg-head">Iceberg Model analysis</div>
                    <div className="cs-iceberg-tiers">
                      {aiData.iceberg.tier1_events?.length > 0 && (
                        <div className="cs-iceberg-tier">
                          <IcebergBadge tier="event" />
                          <ul className="cs-ai-list">
                            {aiData.iceberg.tier1_events.map((e, i) => <li key={i}>{e}</li>)}
                          </ul>
                        </div>
                      )}
                      {aiData.iceberg.tier2_patterns?.length > 0 && (
                        <div className="cs-iceberg-tier">
                          <IcebergBadge tier="pattern" />
                          <ul className="cs-ai-list">
                            {aiData.iceberg.tier2_patterns.map((p, i) => <li key={i}>{p}</li>)}
                          </ul>
                        </div>
                      )}
                      {aiData.iceberg.tier3_hint && (
                        <div className="cs-iceberg-tier">
                          <IcebergBadge tier="structure" />
                          <p className="cs-ai-list-text">{aiData.iceberg.tier3_hint}</p>
                        </div>
                      )}
                      {aiData.iceberg.tier4_hint && (
                        <div className="cs-iceberg-tier">
                          <IcebergBadge tier="mental-model" />
                          <p className="cs-ai-list-text">{aiData.iceberg.tier4_hint}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Auto-create Iceberg cases */}
                {aiData.autoCreateCases?.length > 0 && (
                  <div className="cs-auto-cases">
                    <div className="cs-auto-cases-head">AI-suggested Iceberg cases</div>
                    <div className="cs-auto-cases-list">
                      {aiData.autoCreateCases.map((c, i) => {
                        const pn = c.productNumbers?.[0] || String(i);
                        const alreadyCreated = !!caseCreated[pn];
                        return (
                          <div key={i} className="cs-auto-case-row">
                            <div className="cs-auto-case-info">
                              <span className="cs-auto-case-title">{c.title}</span>
                              {c.productNumbers?.length > 0 && (
                                <span className="cs-auto-case-pn">{c.productNumbers.join(', ')}</span>
                              )}
                              <span className="cs-auto-case-desc">{c.description}</span>
                            </div>
                            {alreadyCreated ? (
                              <span className="cs-auto-case-done">✓ Created</span>
                            ) : (
                              <button
                                className="cs-auto-case-btn"
                                disabled={creatingCase === pn}
                                onClick={() => handleAutoCreate(c)}
                              >
                                {creatingCase === pn ? 'Creating…' : '+ Send to Iceberg'}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
